import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

const API_URL = process.env.REACT_APP_API_URL;

const ProcurementTrackerImport = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const fileInputRef = useRef(null);

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      if (!file) return reject("No file selected");

      const reader = new FileReader();

      reader.onload = () => {
        try {
          const base64 = reader.result.split(",")[1];
          resolve(base64);
        } catch {
          reject("File conversion failed");
        }
      };

      reader.onerror = () => reject("File read error");
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      const ext = "." + file.name.split(".").pop().toLowerCase();

      if (![".xlsx", ".xls"].includes(ext)) {
        toast.error("Please upload Excel file only");
        event.target.value = "";
        return;
      }

      setSelectedFile(file);
      setImportResults(null);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedFile) {
      toast.error("Please select a file");
      return;
    }

    const userId = localStorage.getItem("id");

    setIsLoading(true);
    setImportResults(null);

    try {
      const fileBase64 = await fileToBase64(selectedFile);

      const payload = {
        action: "import-update-procurement",
        fileBase64,
        fileName: selectedFile.name,
        requestUserId: userId,
      };

      const response = await axios.post(API_URL, payload);

      let data = response.data;
      if (typeof data === "string") data = JSON.parse(data);

      if (data.success) {
        const formattedResults = {
          total: data.summary?.total || 0,
          imported: data.summary?.updated || 0,
          skipped: data.summary?.skipped || 0,
          failed: data.summary?.failed || 0,

          importedRecords: data.results
            ?.filter((r) => r.status === "updated")
            .map((r) => ({
              row: r.row,
              prNo: r.prNo || "-",
            })),

          errors: data.results
            ?.filter((r) => r.status !== "updated")
            .map((r) => ({
              row: r.row,
              error: r.message,
            })),
        };

        setImportResults(formattedResults);

        toast.success(data.message);

        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error("Import failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6">
              <h1 className="m-0 txt_lft">Import Procurement</h1>
            </div>
            <div className="col-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/procurementTracker">
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="frmwdth_cntr_640">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-header">
                      <h3 className="card-title">Upload Excel File</h3>
                    </div>

                    <form onSubmit={handleSubmit}>
                      <div className="card-body">
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            value={selectedFile?.name || ""}
                            placeholder="Choose file"
                            readOnly
                          />
                          <div className="input-group-append">
                            <label className="btn btn-blue mb-0">
                              Browse
                              <input
                                type="file"
                                className="d-none"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                              />
                            </label>
                          </div>
                        </div>
                        <small className="form-text text-muted mt-2">
                          Only Excel files (.xlsx, .xls) are supported.
                        </small>
                      </div>

                      <div className="card-footer text-center">
                        <button
                          type="submit"
                          className="btn btn-blue"
                          disabled={!selectedFile || isLoading}
                        >
                          {isLoading ? "Importing..." : "Import"}
                        </button>
                      </div>
                    </form>
                  </div>
                  {importResults && (
                    <div className="card card-primary card-outline mt-3">
                      <div className="card-header">
                        <h3 className="card-title">Import Results</h3>
                      </div>

                      <div className="card-body">
                        <div className="row mb-3">
                          <div className="col-md-3">
                            <div
                              className="info-box"
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                textAlign: "center",
                              }}
                            >
                              <span style={{ fontSize: "13px" }}>Total</span>
                              <span
                                style={{ fontSize: "22px", fontWeight: "bold" }}
                              >
                                {importResults.total}
                              </span>
                            </div>
                          </div>

                          <div className="col-md-3">
                            <div
                              className="info-box"
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                textAlign: "center",
                              }}
                            >
                              <span
                                style={{ fontSize: "13px", color: "green" }}
                              >
                                Updated
                              </span>
                              <span
                                style={{ fontSize: "22px", fontWeight: "bold" }}
                              >
                                {importResults.imported}
                              </span>
                            </div>
                          </div>

                          <div className="col-md-3">
                            <div
                              className="info-box"
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                textAlign: "center",
                              }}
                            >
                              <span
                                style={{ fontSize: "13px", color: "orange" }}
                              >
                                Skipped
                              </span>
                              <span
                                style={{ fontSize: "22px", fontWeight: "bold" }}
                              >
                                {importResults.skipped}
                              </span>
                            </div>
                          </div>

                          <div className="col-md-3">
                            <div
                              className="info-box"
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                textAlign: "center",
                              }}
                            >
                              <span style={{ fontSize: "13px", color: "red" }}>
                                Failed
                              </span>
                              <span
                                style={{ fontSize: "22px", fontWeight: "bold" }}
                              >
                                {importResults.failed}
                              </span>
                            </div>
                          </div>
                        </div>

                        {importResults.importedRecords?.length > 0 && (
                          <div className="mb-4">
                            <h5 className="text-success">Updated Records</h5>
                            <table className="table table-bordered">
                              <thead>
                                <tr>
                                  <th>Row</th>
                                  <th>PR No</th>
                                </tr>
                              </thead>
                              <tbody>
                                {importResults.importedRecords.map((r, i) => (
                                  <tr key={i}>
                                    <td>{r.row}</td>
                                    <td>{r.prNo}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {importResults.errors?.length > 0 && (
                          <div>
                            <h5 className="text-danger">Errors</h5>
                            <table className="table table-bordered">
                              <thead>
                                <tr>
                                  <th>Row</th>
                                  <th>Error</th>
                                </tr>
                              </thead>
                              <tbody>
                                {importResults.errors.map((e, i) => (
                                  <tr key={i}>
                                    <td>{e.row}</td>
                                    <td className="text-danger">{e.error}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcurementTrackerImport;
